"use client"

import { ChevronRight } from "lucide-react"
import { NavLink } from "react-router-dom"
import { Badge } from "@/components/ui/badge"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"

import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui/sidebar"
import { preloadRoute, preloadRoutes } from "@/lib/routePreloader"

export function NavMain({
  items,
  label,
}: {
  items: {
    title: string
    url?: string
    icon?: any
    isActive?: boolean
    badge?: React.ReactNode
    action?: string
    items?: {
      title: string
      url: string
    }[]
  }[]
  label?: string
}) {
  const { isMobile, setOpenMobile } = useSidebar()

  const handleLinkClick = () => {
    if (isMobile) {
      setOpenMobile(false)
    }
  }

  // Preload route chunk on hover
  const handleMouseEnter = (url: string) => {
    preloadRoute(url)
  }

  // Preload all subitems when hovering parent
  const handleGroupHover = (subItems: { url: string }[]) => {
    preloadRoutes(subItems.map(item => item.url))
  }

  return (
    <SidebarGroup className="px-2 py-1">
      {label && (
        <SidebarGroupLabel className="mb-2 px-2 text-xs font-medium text-sidebar-foreground uppercase tracking-wide">
          {label}
        </SidebarGroupLabel>
      )}
      <SidebarMenu className="gap-1">
        {items.map((item) => {
          // Si el item tiene sub-items, usar Collapsible
          if (item.items && item.items.length > 0) {
            return (
              <Collapsible key={item.title} asChild defaultOpen={false} className="group/collapsible">
                <SidebarMenuItem onMouseEnter={() => handleGroupHover(item.items!)}>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton tooltip={item.title} className="h-10 px-3">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        {item.icon && <item.icon className="h-5 w-5 flex-shrink-0" />}
                        <span className="text-sm font-medium truncate group-data-[collapsible=icon]:hidden">{item.title}</span>
                      </div>
                      <ChevronRight className="ml-auto h-4 w-4 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90 group-data-[collapsible=icon]:hidden" />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub>
                      {item.items.map((subItem) => (
                        <SidebarMenuSubItem key={subItem.title}>
                          <SidebarMenuSubButton asChild>
                            <NavLink to={subItem.url} onClick={handleLinkClick} onMouseEnter={() => handleMouseEnter(subItem.url)}>
                              <span>{subItem.title}</span>
                            </NavLink>
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
            )
          }
          
          // Item normal sin sub-items
          if (item.action === "openOnboardingFab") {
            return (
              <SidebarMenuItem key={item.title}>
                <SidebarMenuButton
                  tooltip={item.title}
                  className="h-10 px-3"
                  onClick={() => {
                    window.dispatchEvent(new CustomEvent("onboarding:open"))
                    handleLinkClick()
                  }}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {item.icon && <item.icon className="h-5 w-5 flex-shrink-0" />}
                    <span className="text-sm font-medium truncate group-data-[collapsible=icon]:hidden">{item.title}</span>
                  </div>
                  {item.badge && (
                    <div className="ml-auto group-data-[collapsible=icon]:hidden">{item.badge}</div>
                  )}
                </SidebarMenuButton>
              </SidebarMenuItem>
            )
          }

          return (
            <SidebarMenuItem key={item.title} onMouseEnter={() => item.url && handleMouseEnter(item.url)}>
              <SidebarMenuButton asChild tooltip={item.title} className="h-10 px-3">
                <NavLink to={item.url!} className="flex items-center justify-between" onClick={handleLinkClick}>
                  <div className="flex items-center gap-3 min-w-0">
                    {item.icon && <item.icon className="h-5 w-5 flex-shrink-0" />}
                    <span className="text-sm font-medium truncate group-data-[collapsible=icon]:hidden">{item.title}</span>
                  </div>
                  {item.badge && <div className="ml-auto group-data-[collapsible=icon]:hidden">{item.badge}</div>}
                </NavLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )
        })}
      </SidebarMenu>
    </SidebarGroup>
  )
}
